import { ShopModuleType } from '@/api/model/shopModuleType'
import type { BaseShopModuleModel, SimpleGroupResponse } from '@/api/model'

/** Resolves a shop module type to its translated label (injected so this file stays pure). */
export type ShopModuleLabelResolver = (type: ShopModuleType) => string

/**
 * Types that have their own detail page under `/shop-modules/:shopModuleId`. Only
 * FullPayout has one — the payouts endpoint casts to `FullPayoutShopModule` and answers
 * 400 for every other type, so cards of other types must stay non-clickable.
 * Same idea as `IMPLEMENTED_PAGE_TYPES` on the service module grid.
 */
export const TYPES_WITH_DETAIL_PAGE: ReadonlySet<ShopModuleType> = new Set([ShopModuleType.FullPayout])

export function hasDetailPage(type: ShopModuleType): boolean {
  return TYPES_WITH_DETAIL_PAGE.has(type)
}

/**
 * Display order: `priority` ascending. Lower priority is reserved first when the
 * monthly budget is calculated (backend `ShopModulesBudgetLogic` treats modules with a
 * lower priority as already committed), so the list mirrors that order. Modules without
 * a priority go last; ties break by the visible type label to keep the order stable.
 */
export function sortShopModules(
  modules: BaseShopModuleModel[],
  labelOf: ShopModuleLabelResolver,
): BaseShopModuleModel[] {
  return [...modules].sort((a, b) => {
    const priorityA = a.priority ?? Number.MAX_SAFE_INTEGER
    const priorityB = b.priority ?? Number.MAX_SAFE_INTEGER
    if (priorityA !== priorityB) return priorityA - priorityB
    return labelOf(a.type).localeCompare(labelOf(b.type))
  })
}

/**
 * Client-side search. Shop modules have no `name` field, so the type label is the only
 * thing to match against.
 */
export function filterShopModules(
  modules: BaseShopModuleModel[],
  query: string,
  labelOf: ShopModuleLabelResolver,
): BaseShopModuleModel[] {
  const needle = query.trim().toLowerCase()
  if (!needle) return modules
  return modules.filter((module) => labelOf(module.type).toLowerCase().includes(needle))
}

/** Group names for the assignment tooltip. Ids without a matching group are dropped. */
export function resolveGroupNames(
  groupIds: number[] | null | undefined,
  groups: SimpleGroupResponse[],
): string[] {
  return (groupIds ?? [])
    .map((groupId) => groups.find((group) => group.id === groupId)?.name)
    .filter((name): name is string => !!name)
}
